import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FileItem } from 'api/types';
import { theme } from 'styles';

import { ACCEPTED_MEDIA_TYPES } from './constants';
import { FileGalleryModal } from './FileGalleryModal';
import {
  ZoneWrapper,
  OptionRow,
  ActiveOptionRow,
  OptionLabel,
  DividerText,
  HiddenInput,
} from './styled';

interface MediaUploadZoneProps {
  onUpload: (files: FileList) => Promise<void>;
  formType?: 'createDocument';
  categoryIds?: string[];
  onSelectFromGallery?: (files: FileItem[]) => void;
}

export function MediaUploadZone({
  onUpload,
  formType,
  categoryIds = [],
  onSelectFromGallery,
}: MediaUploadZoneProps): ReactElement {
  const { t } = useTranslation();
  const [galleryOpen, setGalleryOpen] = useState(false);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      await onUpload(e.target.files);
      e.target.value = '';
    }
  };

  function handleAdd(files: FileItem[]) {
    onSelectFromGallery?.(files);
  }

  return (
    <>
      <ZoneWrapper>
        <OptionRow htmlFor="media-upload-input">
          <CloudUploadOutlinedIcon sx={{ color: theme.palette.info.main, fontSize: 28 }} />
          <OptionLabel>{t('common.uploadFromComputer')}</OptionLabel>
          <HiddenInput
            id="media-upload-input"
            type="file"
            multiple
            accept={ACCEPTED_MEDIA_TYPES}
            onChange={handleChange}
          />
        </OptionRow>

        {formType === 'createDocument' && (
          <>
            <DividerText>{t('common.or', 'or')}</DividerText>
            <ActiveOptionRow
              onClick={() => setGalleryOpen(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setGalleryOpen(true);
                }
              }}
            >
              <PhotoLibraryOutlinedIcon sx={{ color: theme.palette.info.main, fontSize: 28 }} />
              <OptionLabel>{t('common.selectFromGallery')}</OptionLabel>
            </ActiveOptionRow>
          </>
        )}
      </ZoneWrapper>

      {galleryOpen && (
        <FileGalleryModal
          open={galleryOpen}
          onClose={() => setGalleryOpen(false)}
          categoryIds={categoryIds}
          onAdd={handleAdd}
        />
      )}
    </>
  );
}
